// Leitner spaced repetition, 5 boxes (HANDOVER §6). Box 0 = never seen.
// Right → up a box, but only when the card is due (or new): a same-day re-quiz (a top-up, a card
// re-asked in its session) keeps the box and the date, so the spacing holds (N5). Skip → same
// box, back tomorrow; wrong → box 1. The phone's projection and the server's 12-month trim
// (compact.ts) both fold answers through here, so they agree.
import { addDays } from './time'

/**
 * `skip`: the child passed on the card (WI-08); it keeps its box and earns nothing. There's no
 * self-graded "close" any more (owner, 23 Oct: phrases are a meaning quiz); an answer stored
 * with one before then is read like a skip (`review` treats anything but right/wrong so).
 */
export type Result = 'right' | 'wrong' | 'skip'

export interface CardState {
  box: number
  /** Play-day (YYYY-MM-DD) the card is next due. */
  due: string
  seen: number
  right: number
  lastDay: string
}

/** Days until a card in box n comes back. */
export const INTERVALS = [0, 1, 2, 4, 7, 14] as const
export const MAX_BOX = 5
/** "Learned" for progress bars (e.g. katakana 18 / 46). */
export const LEARNED_BOX = 3

export function review(prev: CardState | undefined, result: Result, day: string): CardState {
  const box = prev?.box ?? 0
  const early = result === 'right' && !!prev && box > 0 && prev.due > day
  const next = early ? box : result === 'right' ? Math.min(MAX_BOX, box + 1) : result === 'wrong' ? 1 : Math.max(1, box)
  const wait = result === 'right' ? INTERVALS[next]! : 1
  return {
    box: next,
    due: early && prev ? prev.due : addDays(day, wait),
    seen: (prev?.seen ?? 0) + 1,
    right: (prev?.right ?? 0) + (result === 'right' ? 1 : 0),
    lastDay: day,
  }
}

export function isDue(s: CardState | undefined, day: string): boolean {
  return !!s && s.box > 0 && s.due <= day
}

export function isLearned(s: CardState | undefined): boolean {
  return !!s && s.box >= LEARNED_BOX
}
